import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

// Import icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import RouteIcon from "@mui/icons-material/Route";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddIcon from "@mui/icons-material/Add";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getUserById } from "../../services/User";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px 0 rgba(0,0,0,0.06)",
  },
}));

const AvatarLarge = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(16),
  height: theme.spacing(16),
  fontSize: "3rem",
  backgroundColor: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
  "& svg": {
    fontSize: "1rem",
    marginRight: theme.spacing(0.75),
    color: theme.palette.primary.main,
  },
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(2.25),
  marginBottom: theme.spacing(1.5),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `user-tab-${index}`,
    "aria-controls": `user-tabpanel-${index}`,
  };
}

function UserUpdate() {
  const history = useHistory();
  const { userId } = useParams();

  // State management
  const [userData, setUserData] = useState(null);
  const [userError, setUserError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get user initials for avatar
  const getInitials = (fname, lname) => {
    return `${fname?.charAt(0) || ""}${lname?.charAt(0) || ""}`;
  };

  // Format phone number
  const formatPhoneNumber = (phoneNumberString) => {
    if (!phoneNumberString) return "N/A";
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumberString;
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch user data
  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      let result = await getUserById(userId);
      if (result !== "Something went wrong...") {
        console.log("Result", result);
          setUserData(result.user);
          console.log("Done...")
      } else {
        setUserError("Unable to fetch user data!");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserError("An error occurred while fetching user data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchUserData();
  }, []);

  // Handle navigate back
  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <StyledPaper elevation={0}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : userError ? (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="error">
            {userError}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      ) : userData ? (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <PersonIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  User Details
                </Typography>
                <Typography variant="body2">
                  View and manage user information
                </Typography>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2}>
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                  >
                    Back
                  </ActionButton>
                </Stack>
              </Grid>
            </Grid>
          </HeaderCard>

          <Grid container spacing={3}>
            {/* Left column - Profile overview */}
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <AvatarLarge>
                    {getInitials(userData.fname, userData.lname)}
                  </AvatarLarge>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {userData.fname} {userData.lname}
                  </Typography>
                  <Chip
                    icon={userData.locked ? <LockIcon /> : <LockOpenIcon />}
                    label={userData.locked ? "Inactive" : "Active"}
                    color={userData.locked ? "error" : "success"}
                    variant={userData.locked ? "outlined" : "filled"}
                    sx={{ mb: 1 }}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    User ID: {userData.userId}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                  <InfoLabel>
                    <EmailIcon /> Email Address
                  </InfoLabel>
                  <InfoValue>{userData.email}</InfoValue>

                  <InfoLabel>
                    <PhoneIcon /> Phone Number
                  </InfoLabel>
                  <InfoValue>{formatPhoneNumber(userData.phone)}</InfoValue>

                  <InfoLabel>
                    <CalendarTodayIcon /> Joined On
                  </InfoLabel>
                  <InfoValue>{formatDate(userData.joinDate)}</InfoValue>

                  <InfoLabel>
                    <AdminPanelSettingsIcon /> Account Status
                  </InfoLabel>
                  <InfoValue>
                    {userData.enabled ? "Enabled" : "Disabled"}
                    {userData.locked ? " (Locked)" : " (Unlocked)"}
                  </InfoValue>
                </Box>
              </InfoCard>
            </Grid>

            {/* Right column - Detailed information */}
            <Grid item xs={12} md={8}>
              <InfoCard>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="salesperson tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <StyledTab
                      label="Roles & Permissions"
                      icon={<AdminPanelSettingsIcon />}
                      iconPosition="start"
                      {...a11yProps(0)}
                    />
                    <StyledTab
                      label="Warehouses"
                      icon={<WarehouseIcon />}
                      iconPosition="start"
                      {...a11yProps(1)}
                    />
                    <StyledTab
                      label="Routes"
                      icon={<RouteIcon />}
                      iconPosition="start"
                      {...a11yProps(2)}
                    />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Assigned Roles
                  </Typography>
                  <Grid container spacing={1} mb={2}>
                    {userData.roles && userData.roles.length > 0 ? (
                      userData.roles.map((role) => (
                        <Grid item key={role.roleId}>
                          <Chip
                            label={role.roleName.replace(/_/g, " ")}
                            color="primary"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          No roles assigned
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Permissions
                  </Typography>
                  <Grid container spacing={1}>
                    {userData.authorities && userData.authorities.length > 0 ? (
                      userData.authorities.map((auth, index) => (
                        <Grid item key={index}>
                          <Chip
                            label={auth.authority.replace(/_/g, " ")}
                            color="secondary"
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 1.5 }}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          No permissions assigned
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <Box
                    sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}
                  >
                    <Grid container spacing={2}>
                      {userData.warehouseList?.length > 0 ? (
                        <>
                          {userData.warehouseList?.map((warehouse, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Card
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 2 }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <WarehouseIcon color="primary" />
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="medium"
                                  >
                                    {warehouse.warehouseName}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ mt: 1 }}
                                >
                                  ID: {warehouse.warehouseId}
                                </Typography>
                              </Card>
                            </Grid>
                          ))}
                        </>
                      ) : (
                        <Grid item xs={12} sm={12}>
                          <Box sx={{ textAlign: "center", py: 4 }}>
                            <WarehouseIcon
                              sx={{
                                fontSize: 48,
                                color: "text.secondary",
                                opacity: 0.5,
                                mb: 2,
                              }}
                            />
                            <Typography variant="body1" color="textSecondary">
                              No warehouses are assigned to this salesperson.
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  <Box
                    sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}
                  >
                    {Array.isArray(userData.retailRoutes) &&
                    userData.retailRoutes?.length > 0 ? (
                      <Grid container spacing={2}>
                        {userData.retailRoutes.map((route, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Card
                              variant="outlined"
                              sx={{ p: 2, borderRadius: 2 }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <RouteIcon color="primary" />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                >
                                  {route.routeName || `Route ${route.routeId}`}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: 1 }}
                              >
                                ID: {route.routeId}
                              </Typography>
                              {route.description && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  {route.description}
                                </Typography>
                              )}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <RouteIcon
                          sx={{
                            fontSize: 48,
                            color: "text.secondary",
                            opacity: 0.5,
                            mb: 2,
                          }}
                        />
                        <Typography variant="body1" color="textSecondary">
                          No routes are assigned to this salesperson.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TabPanel>
              </InfoCard>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="textSecondary">
            No salesperson data found
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      )}
    </StyledPaper>
  );
}

export default UserUpdate;
